<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { ref, computed, onMounted, watch } from "vue";
import type {
  ISatSelectEmits,
  ISatSelectProps,
  ISatButtonCurrentLocationEmits,
} from "@chatfood/design-system";
import {
  AtomText,
  AtomBadge,
  SatSelect,
  AtomButton,
  AtomLoadingSpinner,
  WoLocation,
  SatButtonCurrentLocation,
} from "@chatfood/design-system";
import type { IPickupLocationsProps } from "./props";
import type { IPickupLocationsEmits } from "./emits";
import { useBusinessStore } from "~/stores/business";
import { useGeoIpLocation } from "~/stores/geoIpLocation";
import type { IDomainOutletState } from "~/domain/outlet-state";
import type { IDomainOutletPickup } from "~/domain/outlet-pickup";
import { ETA_PREPARATION_MARGIN } from "~/domain/constants";
import { useLocale } from "~/composables/use-locale";
import { useDomainLabel } from "~/composables/use-domain-label";
import { usePublicConfig } from "~/config";
import { useDirectBackend } from "#imports";

const css = bemBuilder("pickup-locations");

const props = defineProps<IPickupLocationsProps>();
const emits = defineEmits<IPickupLocationsEmits>();

const { t } = useLocale();
const { outletRepo } = useDirectBackend();
const { outletStateMap, packagingLabel } = useDomainLabel();

const business = useBusinessStore();
const geoIpLocation = useGeoIpLocation();

const isLoading = ref(true);
const isLoadingGeolocation = ref(false);

const latitude = ref<number>();
const longitude = ref<number>();

const mapState: Record<IDomainOutletState, (typeof WoLocation)["state"]> = {
  open: "open",
  closed: "close",
  pre_order_only: "soon",
};

const cities = ref<string[]>([]);
const selectedCity = ref<string>();
const fillCities = async () => {
  let data: { name: string }[] = [];

  const res = await outletRepo.getPickupCities({
    businessId: business.id,
    salesChannel: business.salesChannel,
  });

  if (res.data.length) {
    data = res.data;
  }

  if (data && data.length) {
    cities.value.push(t("components.pickup_locations.select_all_cities"));
    cities.value.push(...data.map((c) => c.name));

    const match = cities.value.find(
      (c) => c.toLowerCase() === geoIpLocation.city?.toLowerCase(),
    );

    await onSelectCity(match);
  }
};

async function initGeolocation() {
  isLoadingGeolocation.value = true;

  try {
    const res = await findPosition();
    if (res) setLocation(res.coords);
  } catch {
    //
  } finally {
    isLoadingGeolocation.value = false;
  }
}

function findPosition(): Promise<GeolocationPosition> | undefined {
  const geolocation = window.navigator?.geolocation;

  if (!geolocation) return;

  return new Promise((resolve, reject) => {
    geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
    });
  });
}

function setLocation(coords: Partial<GeolocationCoordinates>) {
  if (!coords.latitude && !coords.longitude) return;

  latitude.value = coords.latitude;
  longitude.value = coords.longitude;

  fillLocations(selectedCity.value);
}

const selectProps = computed<{
  value: ISatSelectProps["value"];
  options: ISatSelectProps["options"];
}>(() => {
  const options = cities.value.map((city, i) => ({
    id: i.toString(),
    name: city,
  }));
  const value =
    options.find((city) => city.name === selectedCity.value) ?? options[0];
  return {
    value,
    options,
  };
});

const locations = ref<IDomainOutletPickup[]>([]);
const fillLocations = async (city?: string) => {
  const res = await outletRepo.getPickupOutlets({
    businessId: business.id,
    salesChannel: business.salesChannel,
    city,
    lat: latitude.value,
    lng: longitude.value,
  });

  locations.value = res.data;

  emits("loadedLocations", locations.value);
};

const hasPackaging = computed(() =>
  locations.value.some((l) => l.acceptsPackaging),
);

const fillState = async () => {
  isLoading.value = true;
  await fillCities();
  isLoading.value = false;
};

onMounted(() => {
  Promise.all([initGeolocation(), fillState()]);
});

const onHandleSelectCity = async (city: ISatSelectEmits["select"][0]) => {
  await onSelectCity(city.id === "0" ? undefined : city.name);
};

const onSelectCity = async (city?: string) => {
  selectedCity.value = city;

  await fillLocations(city);
  emits("selectedCity", city ?? business.country);
};

const onClickLocation = (location: IDomainOutletPickup) => {
  emits("clickedLocation", location);
};

const focusedLocation = ref();
function onFocusLocation(location: IDomainOutletPickup) {
  focusedLocation.value = location;
  emits("focusedLocation", focusedLocation.value);
}

function onBlurLocation() {
  focusedLocation.value = undefined;
}

const distanceFormatted = (distance?: number) => {
  if (typeof distance !== "number") return undefined;

  const distanceKm = distance / 1000;

  return distanceKm < 1
    ? t("components.pickup_locations.x_meters", {
        distance: Math.floor(distance),
      })
    : t("components.pickup_locations.x_kilometers", {
        distance: Math.floor(distanceKm),
      });
};

const { googleMapsApiKey } = usePublicConfig();

const onCurrentLocation = (
  location: ISatButtonCurrentLocationEmits["success"][0],
) => {
  setLocation({
    latitude: location.coordinates.lat,
    longitude: location.coordinates.lng,
  });
};

watch(
  () => business.salesChannel,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      fillLocations(selectedCity.value);
    }
  },
);
</script>

<template>
  <div :class="[css(''), business.isMobileApp && css('', 'mobile-app')]">
    <div v-if="cities.length > 2" :class="css('select')">
      <SatSelect
        searchable
        :disabled="false"
        :append-to-body="false"
        :has-errors="false"
        :options="selectProps.options"
        :value="selectProps.value"
        @select="onHandleSelectCity"
      />
      <SatButtonCurrentLocation
        :api-key="googleMapsApiKey"
        :is-loading="isLoadingGeolocation"
        @success="onCurrentLocation"
      />
    </div>
    <div v-if="isLoading" :class="css('loading')">
      <AtomLoadingSpinner />
    </div>
    <div v-else :class="css('content')">
      <WoLocation
        v-for="(item, index) in locations"
        :key="index"
        :class="css('location')"
        :name="item.name"
        :image="item.photo"
        :address="item.location"
        :address-prefix="distanceFormatted(item.distance)"
        :badge-value="`${index + 1}`"
        :state="mapState[item.state]"
        :state-label="outletStateMap[item.state]"
        :state-description="
          item.state === 'open'
            ? t('components.pickup_locations.preparation', {
                prepStart: item.preparationMinutes,
                prepEnd: item.preparationMinutes + ETA_PREPARATION_MARGIN,
              })
            : ''
        "
        :is-active="props.clickedLocationId === item.outletId"
        @mouse-hover="onFocusLocation(item)"
        @mouse-out="onBlurLocation"
        @click="onClickLocation(item)"
      >
        <div v-if="hasPackaging" :class="css('packaging')">
          <AtomBadge
            size="small"
            emphasis="low"
            state="neutral"
            data-test="packaging-takeaway"
            :value="packagingLabel('takeaway')"
          />
          <AtomBadge
            v-if="item.acceptsPackaging"
            size="small"
            emphasis="low"
            state="neutral"
            data-test="packaging-eatin"
            :value="packagingLabel('eatin')"
          />
        </div>
        <div
          v-if="
            item.state !== 'closed' && props.clickedLocationId === item.outletId
          "
        >
          <AtomButton
            :is-loading="props.isCtaLoading"
            @click="() => emits('selectedLocation', item)"
          >
            {{
              item.state === "open"
                ? t("components.pickup_locations.pickup_cta")
                : t("components.pickup_locations.schedule_cta")
            }}
          </AtomButton>
        </div>
      </WoLocation>
      <AtomText
        v-if="!locations || !locations?.length"
        color="color-text-secondary"
      >
        {{ t("components.pickup_locations.empty_message") }}
      </AtomText>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.pickup-locations {
  display: flex;
  gap: var(--spacing-md);
  flex-direction: column;

  &__content {
    min-height: 104px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  &__loading {
    height: 104px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__select {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
  }

  &__packaging {
    display: flex;
    gap: var(--spacing-sm);
  }

  &__location {
    width: 100%;
    padding-inline: 0;
  }

  &--mobile-app .pickup-locations {
    &__select {
      margin-bottom: 0;
      padding: 0 var(--spacing-md);
    }

    &__location {
      padding-inline: var(--spacing-md);
    }
  }
}
</style>
